import { useEffect, useLayoutEffect, useState } from "react";
import MapCategory from "@components/MapCategory/index.jsx";
import ShipmentDistribution from "@components/templates/Profile/ShipmentDistribution/index.jsx";
import { getChartType, getEventName, getUserActivePlan, prepareLogisticsMapData } from "@helpers/profileHelper.js";
import { logisticsCategoryData, logisticsEvent, profileTypeEndPoint, profileTypeEnum } from "@components/templates/Profile/ProfileHeader/profile-enum.js";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import styles from "./LogisticsOverView.module.scss";
import { logisticsCachingHandler, portCategoryHandler, profileMapDataHandler } from "@redux/LogisticsProfile/logisticsProfileSlice";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { generateTrackingObject } from "@core/global/global-country-functions";
import NoSearchDataComponent from "@shared/country-components/no-search-data";
import { captureException } from "@sentry/react";

const LogisticsOverView = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [mapData, setMapData] = useState([]);
  const [imageData, setImageData] = useState([]);
  const [activeCategory, setActiveCategory] = useState(logisticsCategoryData[0].id);
  const [shipmentDistribution, setShipmentDistribution] = useState([]);
  const [shipmentDistributionLoader, setShipmentDistributionLoader] = useState(true);

  const { companyId, companyName } = useParams();
  const { search: searchUrlParams, pathname } = useLocation();
  const dataSource = new URLSearchParams(searchUrlParams).get("dataSource");
  const {
    portCategory,
    tabDetails: { activePanelId, activeTabId },
    profileRedirectionId,
    logisticsCaching,
  } = useSelector((state) => state.logisticsProfile);
  const dispatch = useDispatch();

  //useLayoutEffect - need error and loading states before rendering due to tab switch
  useLayoutEffect(() => {
    if (activePanelId) {
      const requestPayload = {
        profile_type: profileTypeEnum.logistics,
        _id: companyId,
        category: activePanelId,
        sub_category: activeTabId,
        map_type: activeCategory,
      };
      setLoading(true);
      setError(false);
      if (!portCategory || portCategory !== activePanelId) {
        dispatch(
          profileMapDataHandler({
            loading: true,
          })
        );
      }
      if (logisticsCaching[activeTabId][activeCategory]) {
        const { mapData, imageData } = logisticsCaching[activeTabId][activeCategory];
        let mapDataClone = JSON.parse(JSON.stringify(mapData));
        let imageDataClone = JSON.parse(JSON.stringify(imageData));
        setMapData(mapDataClone);
        setImageData(imageDataClone);
        setLoading(false);
        dispatch(profileMapDataHandler(logisticsCaching?.profileDetails));
        dispatch(portCategoryHandler(activePanelId));
      } else {
        axios
          .post(profileTypeEndPoint.logistics.getMapData, requestPayload)
          .then((res) => {
            const { data, status } = res.data;
            if (status && status.toLowerCase() === "success") {
              setLoading(false);
              setError(false);
              let profileObj = {};
              // create map data for ports ,companies and lanes.
              const { mapData, imageData } = prepareLogisticsMapData(activeCategory, data[activeCategory]);
              setMapData(mapData);
              setImageData(imageData);
              const { reverse_relation, nvocc_count, vocc_count, vessel_count, importers_count, exporters_count, freight_forwarder_count, company_id, slug } = data;
              if (!portCategory || portCategory !== activePanelId) {
                profileObj = {
                  ...data,
                  nvoccCount: nvocc_count ?? 0,
                  voccCount: vocc_count ?? 0,
                  vesselCount: vessel_count ?? 0,
                  importersCount: importers_count ?? 0,
                  exportersCount: exporters_count ?? 0,
                  freightForwarderCount: freight_forwarder_count ?? 0,
                  reverseRelation: reverse_relation ?? true,
                  loading: false,
                  companyId: company_id ?? "",
                  companyName: slug ?? "",
                };
                dispatch(profileMapDataHandler(profileObj));
                dispatch(portCategoryHandler(activePanelId));
              }
              let logisticsCachingClone = JSON.parse(JSON.stringify(logisticsCaching));
              logisticsCachingClone[activeTabId][activeCategory] = { mapData: mapData ? JSON.parse(JSON.stringify(mapData)) : [], imageData: imageData ? JSON.parse(JSON.stringify(imageData)) : undefined };
              if (Object.keys(profileObj)?.length) {
                logisticsCachingClone.profileDetails = JSON.parse(JSON.stringify(profileObj));
              }
              dispatch(logisticsCachingHandler(logisticsCachingClone));
            } else {
              throw new Error("");
            }
          })
          .catch((error) => {
            captureException(error);
            setLoading(false);
            setError(true);
            toast.error("Something went wrong. Please try again later !!!");
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, activePanelId, activeTabId]);

  useLayoutEffect(() => {
    setShipmentDistributionLoader(true);
    setShipmentDistributionLoader(true);
    if (logisticsCaching.shipmentDistribution) {
      let logisticsCachingClone = JSON.parse(JSON.stringify(logisticsCaching));
      setShipmentDistribution(logisticsCachingClone.shipmentDistribution);
      setShipmentDistributionLoader(false);
    } else if (activePanelId && activeTabId) {
      fetchShipmentData();
    }
  }, [activeTabId, activePanelId]);

  const fetchShipmentData = async () => {
    try {
      const requestPayload = {
        profile_type: profileTypeEnum.logistics,
        _id: companyId,
        category: activePanelId,
        sub_category: activeTabId,
        slug: companyName,
      };

      const res = await axios.post(profileTypeEndPoint.getShipmentDistribution, requestPayload);
      const { data } = res.data;
      if (data && Array.isArray(data) && data.length > 0) {
        setShipmentDistribution(data);
        let logisticsCachingClone = JSON.parse(JSON.stringify(logisticsCaching));
        logisticsCachingClone.shipmentDistribution = data;
        dispatch(logisticsCachingHandler(logisticsCachingClone));
      }
    } catch (err) {
      setError(true);
      toast.error("Something went wrong. Please try again later !!!");
    } finally {
      setShipmentDistributionLoader(false);
    }
  };

  const handleCategoryClick = (category) => {
    trademoTrackingEvents(getEventName(profileTypeEnum.logistics, logisticsEvent.overview[category?.eventEnum]), generateTrackingObject(dataSource, pathname, getUserActivePlan()));
    setActiveCategory(category.id);
  };

  useEffect(() => {
    setActiveCategory(logisticsCategoryData[0].id);
  }, [activePanelId]);

  const viewShipmentsEvent = () => {
    trademoTrackingEvents(getEventName(profileTypeEnum.logistics, logisticsEvent.viewShipments), generateTrackingObject(dataSource, pathname, getUserActivePlan()));
  };

  return error ? (
    <div className={styles.noDataContainer}>
      <NoSearchDataComponent content={"Unable to fetch the data at current moment."} />
    </div>
  ) : (
    <div className={styles.tradeOverviewContainer}>
      <MapCategory categoryData={logisticsCategoryData} activeCategory={activeCategory} handleCategoryClick={handleCategoryClick} />
      <div className={styles.renderMapContainer}>{getChartType(activeCategory, mapData, loading, error, imageData)}</div>
      <ShipmentDistribution shipmentDistribution={shipmentDistribution} shipmentDistributionLoader={shipmentDistributionLoader} profileType={profileTypeEnum.logistics} profileId={companyId} category={activePanelId} subCategory={activeTabId} profileName={companyName} viewShipmentsEvent={viewShipmentsEvent} profileRedirectionId={profileRedirectionId} />
    </div>
  );
};

export default LogisticsOverView;
